import { Directive, ElementRef, inject, input, output } from '@angular/core';

import { tap, throttleTime } from 'rxjs';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { map } from 'rxjs/internal/operators/map';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subscription } from 'rxjs/internal/Subscription';

export interface PanEvent {
  srcEvent: PointerEvent;
  deltaX: number;
  deltaY: number;
  type: string;
  direction: number;
  center: { x: number; y: number };
  target: EventTarget; // Target that received the event.
}

@Directive({
  selector: '[pan],pan',
  host: {
    '[style.touchAction]': '"none"', // Disable touch action on the element.
  },
})
export class PanDirective {
  public elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  public preventDefault = input<boolean>(true);

  public panStart = output<PanEvent>();

  public pan = output<PanEvent>();

  public panEnd = output<PanEvent>();

  private subscriptions = new Subscription();

  private startX = 0;

  private startY = 0;

  ngOnInit() {
    const element = this.elementRef.nativeElement;

    const pointerDown$ = fromEvent<PointerEvent>(element, 'pointerdown');
    const pointerMove$ = fromEvent<PointerEvent>(element, 'pointermove');
    const pointerUp$ = fromEvent<PointerEvent>(element, 'pointerup');

    const pan$ = pointerDown$.pipe(
      switchMap((startEvent) => {
        if (this.preventDefault()) {
          startEvent.preventDefault();
        }

        this.startX = startEvent.clientX;
        this.startY = startEvent.clientY;

        this.panStart.emit({
          srcEvent: startEvent,
          target: startEvent.target,
          type: 'panStart',
          deltaX: 0,
          deltaY: 0,
          direction: 0,
          center: { x: this.startX, y: this.startY },
        });

        return pointerMove$.pipe(
          tap((moveEvent) => element.setPointerCapture(moveEvent.pointerId)),
          throttleTime(16), // 60 FPS
          map((moveEvent) => ({
            srcEvent: moveEvent,
            target: moveEvent.target,
            type: 'pan',
            deltaX: moveEvent.clientX - this.startX,
            deltaY: moveEvent.clientY - this.startY,
            direction: 2,
            center: { x: moveEvent.clientX, y: moveEvent.clientY },
          })),
          takeUntil(pointerUp$),
        );
      }),
    );

    this.subscriptions.add(
      pan$.subscribe((event) => {
        this.pan.emit(event); // Emit horizontal movement
      }),
    );

    this.subscriptions.add(
      pointerUp$
        .pipe(
          tap((pointerEvent) => element.releasePointerCapture(pointerEvent.pointerId)), // Release pointer capture
          map((moveEvent) => ({
            srcEvent: moveEvent,
            target: moveEvent.target,
            type: 'panEnd',
            direction: 0,
            deltaX: moveEvent.clientX - this.startX,
            deltaY: moveEvent.clientY - this.startY,
            center: { x: moveEvent.clientX, y: moveEvent.clientY },
          })), // Emit pan move
        )
        .subscribe((event) => {
          this.panEnd.emit(event); // Emit pan end
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
